import type { ReactNode } from 'react';

/**
 * One labelled fact on a detail record — a `<dt>`/`<dd>` pair.
 *
 * ⚠️ IT DOES NOT SUPPLY ITS OWN `<dl>`. The consuming card does, with
 * `className="divide-y divide-border"`, and that is what draws the hairlines between rows.
 * Wrapping each pair in its own list would put a separator around every fact instead of
 * between them, and would break the one-list-per-card semantics a screen reader announces.
 *
 * Promoted from `pages/OrgTree/components/OrgUnitDetail.tsx` at P3a.1, when the arrangement
 * detail became the second consumer — which is the repo's stated bar for moving a
 * page-local component up (CLAUDE.md §2).
 */
export const Attribute = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5">
    <dt className="shrink-0 text-xs text-muted-foreground">{label}</dt>
    <dd className="min-w-0 truncate text-sm">{children}</dd>
  </div>
);

/**
 * The monospace chip a stable identifier is rendered in — a short code, a reference.
 *
 * A class string rather than a component because it is applied to whatever element already
 * exists at the site (a `<span>`, a table cell), and a wrapper would add a node purely to
 * carry four utilities.
 */
export const CODE_CHIP = 'rounded bg-secondary px-1.5 py-0.5 font-mono text-xs text-muted-foreground';
